import { useState } from "react";
import { useTranslation } from "react-i18next";
import Iconify from "src/components/Iconify";

// Card exibido acima da mensagem quando ela chegou por Click-to-WhatsApp
// (bloco `referral` do webhook da Meta). Mostra de qual anuncio/publicacao
// o contato veio antes de mandar a primeira mensagem.
export const ReferralCard = ({ referral }) => {
  const { t } = useTranslation('chat');
  const [imageError, setImageError] = useState(false);

  if (!referral) return null;

  const {
    source_type: sourceType,
    source_id: sourceId,
    source_url: sourceUrl,
    headline,
    body,
    media_type: mediaType,
    image_url: imageUrl,
    thumbnail_url: thumbnailUrl,
    video_url: videoUrl,
    welcome_message: welcomeMessage,
  } = referral;

  // Sem nenhum dado util nao vale ocupar espaco no balao.
  if (!sourceId && !sourceUrl && !headline && !body) return null;

  const isPost = sourceType === 'post';
  const thumb = thumbnailUrl || imageUrl || (mediaType === 'image' ? videoUrl : null);
  const isVideo = mediaType === 'video';
  const welcomeText = typeof welcomeMessage === 'string' ? welcomeMessage : welcomeMessage?.text;

  return (
    <div className="mb-1.5 w-full max-w-[300px] overflow-hidden rounded-xl border border-violet-200/70 dark:border-violet-500/25 bg-violet-50/70 dark:bg-violet-500/[0.08]">

      <div className="flex items-center gap-1.5 px-2 py-1.5 border-b border-violet-200/60 dark:border-violet-500/20">
        <div className="w-5 h-5 rounded-md bg-violet-100 dark:bg-violet-500/20 flex items-center justify-center flex-shrink-0">
          <Iconify
            icon={isPost ? "mdi:post-outline" : "mdi:bullhorn-variant-outline"}
            width={12}
            className="text-violet-600 dark:text-violet-300"
          />
        </div>
        <span className="text-[11px] font-semibold text-violet-700 dark:text-violet-300 truncate">
          {isPost
            ? t('referral.fromPost', { defaultValue: 'Veio de uma publicação' })
            : t('referral.fromAd', { defaultValue: 'Veio de um anúncio' })}
        </span>
      </div>

      {thumb && !imageError && (
        <div className="relative bg-black/5 dark:bg-white/5">
          <img
            src={thumb}
            alt={headline || ''}
            onError={() => setImageError(true)}
            className="w-full max-h-32 object-cover"
          />
          {isVideo && (
            <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
              <div className="w-8 h-8 rounded-full bg-black/55 flex items-center justify-center">
                <Iconify icon="solar:play-bold" width={16} className="text-white" />
              </div>
            </div>
          )}
        </div>
      )}

      {(headline || body) && (
        <div className="px-2.5 py-2 flex flex-col gap-0.5">
          {headline && (
            <p className="text-[12px] font-semibold leading-snug text-gray-800 dark:text-neutral-100 break-words">
              {headline}
            </p>
          )}
          {body && (
            <p className="text-[11px] leading-snug text-gray-500 dark:text-neutral-400 break-words line-clamp-3">
              {body}
            </p>
          )}
        </div>
      )}

      {welcomeText && (
        <div className="px-2.5 pb-2 -mt-0.5">
          <p className="text-[10px] italic leading-snug text-gray-400 dark:text-neutral-500 break-words line-clamp-2">
            “{welcomeText}”
          </p>
        </div>
      )}

      {(sourceId || sourceUrl) && (
        <div className="flex items-center justify-between gap-2 px-2.5 py-1.5 border-t border-violet-200/60 dark:border-violet-500/20">
          {sourceId ? (
            <span
              title={sourceId}
              className="text-[10px] font-mono text-gray-400 dark:text-neutral-500 truncate"
            >
              ID {sourceId}
            </span>
          ) : <span />}

          {sourceUrl && (
            <a
              href={sourceUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-1 text-[10px] font-semibold text-violet-600 dark:text-violet-300 hover:underline flex-shrink-0"
            >
              {isPost
                ? t('referral.viewPost', { defaultValue: 'Ver publicação' })
                : t('referral.viewAd', { defaultValue: 'Ver anúncio' })}
              <Iconify icon="eva:external-link-fill" width={11} />
            </a>
          )}
        </div>
      )}
    </div>
  );
};

export default ReferralCard;
